import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import usePageTitle from "../hooks/usePageTitle";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Phone, Loader2, RefreshCw, DownloadCloud, Sparkles, Clock, ListChecks, Tag,
  BadgeCheck, Activity, MessageSquare, CheckCircle2, XCircle, HelpCircle,
  ChevronDown, ChevronRight, FileText,
} from "lucide-react";

// ---- shared visual helpers (mirrors the /roleplay ScoreCard) ----

function scoreColor(v) {
  if (v >= 75) return "text-emerald-600";
  if (v >= 50) return "text-amber-600";
  return "text-red-600";
}

function fmtDur(s) {
  s = s || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Run async `worker` over `items` with bounded concurrency — fire many requests
// at once instead of one-at-a-time, without slamming provider rate limits.
// Returns {ok, fail}; never rejects (a failing item just counts as fail).
async function runPool(items, worker, concurrency, onProgress) {
  let cursor = 0, done = 0, ok = 0, fail = 0;
  const runner = async () => {
    while (cursor < items.length) {
      const item = items[cursor++];
      try { await worker(item); ok++; } catch { fail++; }
      onProgress?.(++done, items.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, runner));
  return { ok, fail };
}

const ANALYZE_CONCURRENCY = 6;    // parallel Opus scorings (bounded for rate limits)
const CLASSIFY_CONCURRENCY = 12;  // parallel cheap classifications

function MetricCard({ label, value, hint }) {
  return (
    <div className="border rounded-lg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

// The exact breakdown card used on /roleplay — overall, Feedback / Analytics /
// Objections tabs, scorecard and rubric criteria. Kept self-contained on purpose
// so this page can't break the trainer.
function ScoreCard({ score }) {
  if (!score) return null;
  const a = score.analytics || {};
  const byCat = {};
  (score.scorecard || []).forEach((it) => {
    (byCat[it.category] = byCat[it.category] || []).push(it);
  });
  return (
    <Card className="border-2">
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-start gap-4">
          <div className="text-center shrink-0">
            <div className={`text-5xl font-bold tabular-nums ${scoreColor(score.overall)}`}>{score.overall}</div>
            <div className="text-[11px] text-muted-foreground">overall · 0–100</div>
          </div>
          <div className="min-w-0">
            {score.summary && <p className="text-sm">{score.summary}</p>}
            {score.model && <div className="text-[11px] text-muted-foreground mt-1">{score.model}</div>}
          </div>
        </div>

        <Tabs defaultValue="feedback">
          <TabsList>
            <TabsTrigger value="feedback"><BadgeCheck className="h-4 w-4 mr-1" />Feedback</TabsTrigger>
            <TabsTrigger value="analytics"><Activity className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="objections"><MessageSquare className="h-4 w-4 mr-1" />Objections</TabsTrigger>
          </TabsList>

          <TabsContent value="feedback" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-semibold text-emerald-600 mb-1">What went well</div>
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {(score.did_well || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold text-red-600 mb-1">To improve</div>
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {(score.did_wrong || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            </div>
            {score.top_recommendation && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-sm">
                <span className="font-semibold">Top recommendation: </span>{score.top_recommendation}
              </div>
            )}
            {Object.keys(byCat).length > 0 && (
              <div className="space-y-3">
                <div className="text-sm font-semibold">Scorecard</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(byCat).map(([cat, items]) => (
                    <div key={cat} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{cat}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {items.filter((x) => x.passed).length}/{items.length}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {items.map((it, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm" title={it.note}>
                            {it.passed
                              ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                              : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                            <span className={it.passed ? "" : "text-muted-foreground"}>{it.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(score.criteria || []).map((c) => (
                <div key={c.key} className="border rounded-md p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{c.label}</span>
                    <span className={`font-bold tabular-nums ${scoreColor(c.score * 10)}`}>{c.score}/10</span>
                  </div>
                  {c.note && <div className="text-xs text-muted-foreground mt-1">{c.note}</div>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Talk / listen" value={`${a.talk_listen_pct ?? 0}%`} hint="rep share of words" />
              <MetricCard label="Talk speed" value={`${a.talk_speed_wpm ?? 0} wpm`} hint="approx." />
              <MetricCard label="Filler words" value={a.filler_count ?? 0} hint="um / yani / ну…" />
              <MetricCard label="Longest monologue" value={`${a.longest_monologue_words ?? 0}w`} hint="single turn" />
              <MetricCard label="Call length" value={fmtDur(a.duration_sec)} />
              <MetricCard label="Rep words" value={a.rep_words ?? 0} />
              <MetricCard label="Client words" value={a.buyer_words ?? 0} />
              <MetricCard label="Rep turns" value={a.rep_turns ?? 0} />
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Talk speed is approximate. Aim for a balanced talk/listen ratio and short monologues.
            </p>
          </TabsContent>

          <TabsContent value="objections" className="space-y-4">
            <div>
              <div className="text-sm font-semibold mb-1.5">Client objections &amp; how the rep handled them</div>
              {(score.objections || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No objections identified.</div>
              ) : (
                <div className="space-y-2">
                  {score.objections.map((o, i) => (
                    <div key={i} className="border rounded-md p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium">{o.objection}</span>
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${
                          o.handled === "yes" ? "text-emerald-600" : o.handled === "partly" ? "text-amber-600" : "text-red-600"
                        }`}>{o.handled}</Badge>
                      </div>
                      {o.note && <div className="text-xs text-muted-foreground mt-1">{o.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                <HelpCircle className="h-4 w-4" /> Discovery questions the rep asked
              </div>
              {(score.questions_asked || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No discovery questions identified.</div>
              ) : (
                <ul className="text-sm space-y-1 list-disc pl-4">
                  {score.questions_asked.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Sales / not-sales tag cell. Shows a badge once classified, or a "tag" button.
function TypeCell({ call, busy, onClassify }) {
  if (call.call_type === "sales")
    return <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-600/40" title={call.call_type_reason || ""}>Sales</Badge>;
  if (call.call_type === "not_sales")
    return <Badge variant="outline" className="text-[10px] text-muted-foreground" title={call.call_type_reason || ""}>Not sales</Badge>;
  return (
    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-muted-foreground" onClick={onClassify} disabled={busy}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Tag className="h-3 w-3 mr-1" />tag</>}
    </Button>
  );
}

export default function CallReviewsPage() {
  usePageTitle("Call Reviews");
  const { user } = useAuth();
  const canSync = ["admin", "sales_director"].includes(user?.role);

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});   // recId -> { review, transcript_text, loading }
  const [busy, setBusy] = useState({});         // recId -> bool (single-row analyzing)
  const [showTx, setShowTx] = useState({});     // recId -> bool (transcript toggle)
  const [bulk, setBulk] = useState(null);       // { done, total } while "Analyze all" runs
  const [classBusy, setClassBusy] = useState({}); // recId -> bool (single-row classifying)
  const [bulkClass, setBulkClass] = useState(null); // { done, total } while "Classify all" runs
  const [filter, setFilter] = useState("all");  // all | sales | not_sales | unclassified

  const load = useCallback(() => {
    setLoading(true);
    api.get("/call-reviews/calls")
      .then((r) => setCalls(r.data.calls || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load calls"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Lazy-load the cached review + transcript for one row.
  const fetchDetail = useCallback((recId) => {
    setDetails((d) => ({ ...d, [recId]: { ...(d[recId] || {}), loading: true } }));
    api.get(`/call-reviews/calls/${encodeURIComponent(recId)}`)
      .then((r) => setDetails((d) => ({ ...d, [recId]: { ...r.data, loading: false } })))
      .catch(() => setDetails((d) => ({ ...d, [recId]: { ...(d[recId] || {}), loading: false } })));
  }, []);

  const toggle = (recId) => {
    const next = expanded === recId ? null : recId;
    setExpanded(next);
    if (next && !details[recId]) fetchDetail(recId);
  };

  // Score one call; updates the row's overall + cached review. Throws on failure.
  const analyzeOne = useCallback(async (recId, force = false) => {
    const r = await api.post(`/call-reviews/calls/${encodeURIComponent(recId)}/analyze`, { force });
    const review = r.data.review;
    setDetails((d) => ({ ...d, [recId]: { ...(d[recId] || {}), review, rep_names: r.data.rep_names } }));
    setCalls((cs) => cs.map((c) => c.recording_id === recId
      ? { ...c, overall: review.overall, has_review: true } : c));
    return review.overall;
  }, []);

  const analyzeRow = async (recId, force = false) => {
    setBusy((b) => ({ ...b, [recId]: true }));
    try {
      await analyzeOne(recId, force);
      setExpanded(recId);
      if (!details[recId]) fetchDetail(recId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy((b) => ({ ...b, [recId]: false }));
    }
  };

  // Analyze every not-yet-scored call, fired in parallel (bounded concurrency).
  const analyzeAll = async () => {
    const pending = calls.filter((c) => !c.has_review);
    if (pending.length === 0) { toast.info("All calls already analyzed"); return; }
    if (!window.confirm(`Run AI scoring on ${pending.length} un-analyzed call(s)? Runs ${ANALYZE_CONCURRENCY} at a time.`)) return;
    setBulk({ done: 0, total: pending.length });
    const { ok, fail } = await runPool(pending, (c) => analyzeOne(c.recording_id),
      ANALYZE_CONCURRENCY, (done, total) => setBulk({ done, total }));
    setBulk(null);
    toast[fail ? "warning" : "success"](`Analyzed ${ok}/${pending.length}${fail ? ` · ${fail} failed` : ""}`);
  };

  // Tag one call sales / not_sales; updates the row.
  const classifyOne = useCallback(async (recId, force = false) => {
    const r = await api.post(`/call-reviews/calls/${encodeURIComponent(recId)}/classify${force ? "?force=true" : ""}`);
    setCalls((cs) => cs.map((c) => c.recording_id === recId
      ? { ...c, call_type: r.data.call_type, call_type_reason: r.data.call_type_reason } : c));
    return r.data.call_type;
  }, []);

  const classifyRow = async (recId, force = false) => {
    setClassBusy((b) => ({ ...b, [recId]: true }));
    try { await classifyOne(recId, force); }
    catch (e) { toast.error(e?.response?.data?.detail || "Classification failed"); }
    finally { setClassBusy((b) => ({ ...b, [recId]: false })); }
  };

  // Tag every still-untagged call, fired in parallel (cheap model → high concurrency).
  const classifyAll = async () => {
    const pending = calls.filter((c) => !c.call_type);
    if (pending.length === 0) { toast.info("All calls already tagged"); return; }
    setBulkClass({ done: 0, total: pending.length });
    const { ok, fail } = await runPool(pending, (c) => classifyOne(c.recording_id),
      CLASSIFY_CONCURRENCY, (done, total) => setBulkClass({ done, total }));
    setBulkClass(null);
    toast[fail ? "warning" : "success"](`Tagged ${ok}/${pending.length}${fail ? ` · ${fail} failed` : ""}`);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/integrations/fellow/sync-transcripts?limit=400");
      toast.success(`Synced — ${r.data.upserted_transcripts} new, ${r.data.skipped_already_cached} already cached`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fellow sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const reviewed = calls.filter((c) => c.has_review).length;
  const pendingCount = calls.length - reviewed;
  const untagged = calls.filter((c) => !c.call_type).length;
  const shown = calls.filter((c) =>
    filter === "all" ? true
      : filter === "unclassified" ? !c.call_type
        : c.call_type === filter);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="h-6 w-6" /> Call Reviews
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            AI breakdown of real sales calls recorded in Fellow — same scorecard,
            objections and feedback as the <a href="/roleplay" className="underline">Roleplay Trainer</a>.
            Only external/sales calls are shown (a staff rep + a client on the line).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load} disabled={loading || !!bulk}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {canSync && (
            <Button variant="outline" size="sm" onClick={sync} disabled={syncing || !!bulk}>
              {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-1" />}
              Pull from Fellow
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={classifyAll} disabled={!!bulkClass || !!bulk || loading || untagged === 0}>
            {bulkClass
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Tagging {bulkClass.done}/{bulkClass.total}…</>
              : <><Tag className="h-4 w-4 mr-1" /> Classify all{untagged > 0 ? ` (${untagged})` : ""}</>}
          </Button>
          <Button size="sm" onClick={analyzeAll} disabled={!!bulk || !!bulkClass || loading || pendingCount === 0}>
            {bulk
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analyzing {bulk.done}/{bulk.total}…</>
              : <><ListChecks className="h-4 w-4 mr-1" /> Analyze all{pendingCount > 0 ? ` (${pendingCount})` : ""}</>}
          </Button>
        </div>
      </div>

      {!loading && calls.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-muted-foreground">
            {calls.length} call{calls.length === 1 ? "" : "s"} · {reviewed} analyzed · {pendingCount} pending
            {untagged > 0 && ` · ${untagged} untagged`}
          </div>
          <div className="flex items-center gap-1">
            {[["all", "All"], ["sales", "Sales"], ["not_sales", "Not sales"], ["unclassified", "Untagged"]].map(([k, lbl]) => {
              const n = k === "all" ? calls.length
                : k === "unclassified" ? untagged
                  : calls.filter((c) => c.call_type === k).length;
              return (
                <Button key={k} size="sm" variant={filter === k ? "default" : "outline"}
                  className="h-7 px-2 text-xs" onClick={() => setFilter(k)}>
                  {lbl} <span className="ml-1 opacity-60 tabular-nums">{n}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading calls…
        </div>
      ) : calls.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground space-y-2">
            <Phone className="h-8 w-8 mx-auto opacity-40" />
            <p>No external sales calls found in the Fellow cache yet.</p>
            {canSync && <p className="text-xs">Click <b>Pull from Fellow</b> to sync the latest recordings.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
                <th className="w-6 px-2 py-2"></th>
                <th className="px-2 py-2 font-medium whitespace-nowrap">Date</th>
                <th className="px-2 py-2 font-medium">Rep · who speaks</th>
                <th className="px-2 py-2 font-medium">Client · with whom</th>
                <th className="px-2 py-2 font-medium">Contact / topic</th>
                <th className="px-2 py-2 font-medium">Type</th>
                <th className="px-2 py-2 font-medium text-right">CRM</th>
                <th className="px-2 py-2 font-medium text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                const isOpen = expanded === c.recording_id;
                const det = details[c.recording_id];
                const review = det?.review;
                const rowBusy = !!busy[c.recording_id];
                return (
                  <React.Fragment key={c.recording_id}>
                    <tr
                      onClick={() => toggle(c.recording_id)}
                      className={`border-b cursor-pointer hover:bg-muted/40 transition-colors ${isOpen ? "bg-muted/30" : ""}`}>
                      <td className="px-2 py-2 align-top text-muted-foreground">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-2 py-2 align-top whitespace-nowrap">
                        <div>{fmtDate(c.started_at)}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          {c.duration_sec > 0 && <><Clock className="h-3 w-3" />{fmtDur(c.duration_sec)}</>}
                          {c.language_code && <span className="uppercase ml-1">{c.language_code}</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">{(c.rep_names || []).join(", ") || "—"}</td>
                      <td className="px-2 py-2 align-top">{(c.client_names || []).join(", ") || "—"}</td>
                      <td className="px-2 py-2 align-top max-w-xs">
                        <div className="truncate" title={c.title}>{c.title}</div>
                      </td>
                      <td className="px-2 py-2 align-top whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <TypeCell call={c} busy={!!classBusy[c.recording_id] || !!bulkClass}
                          onClassify={() => classifyRow(c.recording_id)} />
                      </td>
                      {/* The CRM's own grading (0–10). Shown next to ours, not
                          instead of it: sales already works from this number, and
                          a divergence between the two columns is itself signal.
                          A "~" flags a same-day match rather than an id hit. */}
                      <td className="px-2 py-2 align-top text-right whitespace-nowrap">
                        {c.crm_score != null ? (
                          <span
                            className="inline-flex items-center gap-1"
                            title={[
                              c.crm_top_violation,
                              c.crm_match === "id" ? "matched by recording id" : "matched by rep + day",
                            ].filter(Boolean).join(" — ")}
                          >
                            <span className={`text-sm font-semibold tabular-nums ${
                              c.crm_score >= 8 ? "text-emerald-500"
                                : c.crm_score >= 6 ? "text-amber-500" : "text-red-500"}`}>
                              {c.crm_score}
                            </span>
                            {c.crm_match !== "id" && <span className="text-muted-foreground text-[10px]">~</span>}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {c.overall != null ? (
                          <span className={`text-lg font-bold tabular-nums ${scoreColor(c.overall)}`}>{c.overall}</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                            onClick={() => analyzeRow(c.recording_id)} disabled={rowBusy || !!bulk}>
                            {rowBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                            Analyze
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b bg-muted/10">
                        <td colSpan={8} className="px-4 py-4">
                          {det?.loading ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="flex flex-wrap items-center gap-2">
                                <Button size="sm" onClick={() => analyzeRow(c.recording_id, false)} disabled={rowBusy}>
                                  {rowBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                                  {review ? "Re-open analysis" : "Analyze call"}
                                </Button>
                                {review && (
                                  <Button size="sm" variant="outline" onClick={() => analyzeRow(c.recording_id, true)} disabled={rowBusy}>
                                    <RefreshCw className="h-4 w-4 mr-1" /> Re-analyze
                                  </Button>
                                )}
                                {det?.transcript_text && (
                                  <Button size="sm" variant="ghost"
                                    onClick={() => setShowTx((s) => ({ ...s, [c.recording_id]: !s[c.recording_id] }))}>
                                    <FileText className="h-4 w-4 mr-1" /> {showTx[c.recording_id] ? "Hide" : "Show"} transcript
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => classifyRow(c.recording_id, true)}
                                  disabled={!!classBusy[c.recording_id]}>
                                  {classBusy[c.recording_id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Tag className="h-4 w-4 mr-1" />}
                                  {c.call_type ? "Re-classify" : "Classify type"}
                                </Button>
                              </div>
                              {c.call_type && (
                                <div className="text-xs text-muted-foreground">
                                  Type: <span className={c.call_type === "sales" ? "text-emerald-600 font-medium" : "font-medium"}>
                                    {c.call_type === "sales" ? "Sales call" : "Not a sales call"}</span>
                                  {(det?.call_type_reason || c.call_type_reason) && ` — ${det?.call_type_reason || c.call_type_reason}`}
                                </div>
                              )}
                              {review ? <ScoreCard score={review} /> : (
                                <p className="text-sm text-muted-foreground">
                                  Not analyzed yet — click <b>Analyze call</b> to run the same AI breakdown as the roleplay trainer.
                                </p>
                              )}
                              {showTx[c.recording_id] && det?.transcript_text && (
                                <pre className="text-xs whitespace-pre-wrap bg-muted/40 rounded-md p-3 max-h-96 overflow-auto">
                                  {det.transcript_text}
                                </pre>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
